import { useState, useEffect } from 'react';
import { Skull, X, Loader2, AlertOctagon, Users, MessageSquare, Calendar, Shield, Gamepad2, Smartphone, Coins } from 'lucide-react';
import type { UserDetail } from '../UserDetailPage';
import { useDangerZone } from '../../../contexts/ReauthContext';

interface DeletionPreview {
  user: { id: number; firstName: string; lastName: string; email: string; profileName: string | null };
  squadMembershipsCount: number;
  ownedSquadsCount: number;
  ownedSquadNames: string[];
  squadMessagesCount: number;
  sessionsCreatedCount: number;
  sessionsRsvpCount: number;
  reportsAgainstCount: number;
  reportsFiledCount: number;
  userBlocksCount: number;
  matchesCount: number;
  leagueRostersCount: number;
  deviceRegistrationsCount: number;
  ledgerTransactionsCount: number;
  hasCognitoAccount: boolean;
  hasAvatarFiles: boolean;
}

interface Props {
  user: UserDetail;
  onClose: () => void;
  onSuccess: () => void;
}

interface ExternalCleanupReport {
  success?: boolean;
  cognitoAttempted?: boolean;
  cognitoDeleted?: boolean;
  cognitoError?: string;
  s3Attempted?: boolean;
  s3Deleted?: boolean;
  s3Error?: string;
  dynamoAttempted?: boolean;
  dynamoPresenceDeleted?: number;
  dynamoStateDeleted?: number;
  dynamoMessagesDeleted?: number;
  dynamoIdempotencyDeleted?: number;
  dynamoError?: string;
}

interface HardDeleteResponse {
  success: boolean;
  message: string;
  userId: number;
  externalCleanup?: ExternalCleanupReport;
}

export function HardDeleteModal({ user, onClose, onSuccess }: Props) {
  const { executeDangerZone } = useDangerZone();
  const [step, setStep] = useState<'preview' | 'confirm' | 'result'>('preview');
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<HardDeleteResponse | null>(null);

  const confirmTarget = user.email || user.profileName || '';
  const confirmMatches = confirmText.toLowerCase() === confirmTarget.toLowerCase();
  const canDelete = reason.trim().length > 0 && confirmMatches;

  useEffect(() => {
    async function loadPreview() {
      setIsLoadingPreview(true);
      setPreviewError(null);
      try {
        const res = await fetch(`/api/v1/admin/users/${user.id}/deletion-preview`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        setPreview(data);
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
      } finally {
        setIsLoadingPreview(false);
      }
    }
    loadPreview();
  }, [user.id]);

  const handleDelete = async () => {
    if (!canDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await executeDangerZone((dangerHeaders) =>
        fetch(`/api/v1/admin/users/${user.id}/hard-delete`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...dangerHeaders,
          },
          body: JSON.stringify({ reason, confirmationText: confirmText }),
        })
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Failed: ${res.status}`);
      }
      const data = (await res.json()) as HardDeleteResponse;
      setDeleteResult(data);
      setStep('result');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete user';
      if (msg === 'Re-authentication cancelled') {
        setDeleteError('Re-authentication cancelled. Hard delete was not executed.');
      } else {
        setDeleteError(msg);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const totalRecords = preview
    ? preview.squadMembershipsCount + preview.squadMessagesCount + preview.sessionsCreatedCount +
      preview.sessionsRsvpCount + preview.reportsAgainstCount + preview.reportsFiledCount +
      preview.userBlocksCount + preview.matchesCount + preview.leagueRostersCount +
      preview.deviceRegistrationsCount + preview.ledgerTransactionsCount + preview.ownedSquadsCount
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 border border-red-800/50 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-red-800/50 bg-red-900/20 rounded-t-xl">
          <div className="flex items-center gap-3">
            <Skull className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-semibold text-red-400">
              {step === 'preview'
                ? 'Deletion Preview'
                : step === 'confirm'
                  ? 'Confirm Hard Delete'
                  : 'Deletion Result'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-700 rounded transition-colors">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {step === 'preview' && (
            <>
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertOctagon className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-300">
                      You are about to permanently delete <strong>{user.firstName} {user.lastName}</strong> and all their data.
                    </p>
                    <p className="text-xs text-red-400 mt-1">This action is IRREVERSIBLE. All data shown below will be permanently removed.</p>
                  </div>
                </div>
              </div>

              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-slate-500 animate-spin" />
                  <span className="ml-3 text-slate-400">Loading dependent data...</span>
                </div>
              ) : previewError ? (
                <div className="px-4 py-3 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{previewError}</div>
              ) : preview ? (
                <>
                  <div className="text-center py-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <span className="text-3xl font-bold text-red-400">{totalRecords}</span>
                    <p className="text-xs text-slate-500 mt-1">total records to be deleted</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <PreviewCard icon={<Users className="h-4 w-4" />} label="Squad Memberships" count={preview.squadMembershipsCount} />
                    <PreviewCard icon={<Users className="h-4 w-4" />} label="Owned Squads" count={preview.ownedSquadsCount}
                      detail={preview.ownedSquadNames.length > 0 ? preview.ownedSquadNames.join(', ') : undefined} />
                    <PreviewCard icon={<MessageSquare className="h-4 w-4" />} label="Chat Messages" count={preview.squadMessagesCount} />
                    <PreviewCard icon={<Calendar className="h-4 w-4" />} label="Sessions Created" count={preview.sessionsCreatedCount} />
                    <PreviewCard icon={<Calendar className="h-4 w-4" />} label="Session RSVPs" count={preview.sessionsRsvpCount} />
                    <PreviewCard icon={<Shield className="h-4 w-4" />} label="Reports (Against / Filed)" count={preview.reportsAgainstCount + preview.reportsFiledCount}
                      detail={`${preview.reportsAgainstCount} against, ${preview.reportsFiledCount} filed`} />
                    <PreviewCard icon={<Gamepad2 className="h-4 w-4" />} label="Matches" count={preview.matchesCount} />
                    <PreviewCard icon={<Users className="h-4 w-4" />} label="League Rosters" count={preview.leagueRostersCount} />
                    <PreviewCard icon={<Smartphone className="h-4 w-4" />} label="Device Registrations" count={preview.deviceRegistrationsCount} />
                    <PreviewCard icon={<Coins className="h-4 w-4" />} label="Ledger Transactions" count={preview.ledgerTransactionsCount} />
                  </div>

                  {/* External systems */}
                  <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                    <h4 className="text-xs font-medium text-slate-500 uppercase">External Systems</h4>
                    <div className="flex gap-3 text-sm">
                      <span className={preview.hasCognitoAccount ? 'text-amber-400' : 'text-slate-500'}>
                        Cognito: {preview.hasCognitoAccount ? 'Will be included in best-effort cleanup' : 'N/A'}
                      </span>
                    </div>
                    <div className="flex gap-3 text-sm">
                      <span className={preview.hasAvatarFiles ? 'text-amber-400' : 'text-slate-500'}>
                        S3 Avatar: {preview.hasAvatarFiles ? 'Will be included in best-effort cleanup' : 'N/A'}
                      </span>
                    </div>
                    <div className="flex gap-3 text-sm">
                      <span className="text-amber-400">DynamoDB: Best-effort chat/presence cleanup</span>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4">
                <p className="text-sm text-red-300 font-medium">
                  This will permanently delete {user.firstName} {user.lastName} and {totalRecords} associated records. This cannot be undone.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Reason for deletion <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  placeholder="e.g., User requested complete data deletion per email on..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-red-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Type <code className="px-1.5 py-0.5 bg-slate-700 rounded text-red-400 text-xs">{confirmTarget}</code> to confirm <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={confirmTarget}
                  className={`w-full px-3 py-2 bg-slate-700 border rounded-lg text-white text-sm focus:outline-none ${
                    confirmText && !confirmMatches ? 'border-red-500' : 'border-slate-600 focus:border-red-500'
                  }`}
                />
                {confirmText && !confirmMatches && (
                  <p className="text-xs text-red-400 mt-1">Text does not match</p>
                )}
              </div>

              {deleteError && (
                <div className="px-3 py-2 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{deleteError}</div>
              )}
            </>
          )}

          {step === 'result' && deleteResult && (
            <>
              <div className={`border rounded-lg p-4 ${
                deleteResult.success ? 'bg-emerald-900/20 border-emerald-800/40' : 'bg-red-900/20 border-red-800/40'
              }`}>
                <p className={`text-sm font-medium ${deleteResult.success ? 'text-emerald-300' : 'text-red-300'}`}>
                  {deleteResult.message || 'Hard delete completed'}
                </p>
                <p className="text-xs text-slate-400 mt-1">User ID: {deleteResult.userId}</p>
              </div>

              {deleteResult.externalCleanup && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-300">External Cleanup Report</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase">Cognito</div>
                      <div className="mt-1 text-sm text-slate-200">
                        {deleteResult.externalCleanup.cognitoAttempted
                          ? (deleteResult.externalCleanup.cognitoDeleted ? 'Deleted' : 'Not deleted')
                          : 'Not applicable'}
                      </div>
                      {deleteResult.externalCleanup.cognitoError && (
                        <div className="text-xs text-red-400 mt-1 break-words">{deleteResult.externalCleanup.cognitoError}</div>
                      )}
                    </div>

                    <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase">S3 Avatar</div>
                      <div className="mt-1 text-sm text-slate-200">
                        {deleteResult.externalCleanup.s3Attempted
                          ? (deleteResult.externalCleanup.s3Deleted ? 'Deleted' : 'Not deleted')
                          : 'Not applicable'}
                      </div>
                      {deleteResult.externalCleanup.s3Error && (
                        <div className="text-xs text-red-400 mt-1 break-words">{deleteResult.externalCleanup.s3Error}</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg p-3">
                    <div className="text-xs text-slate-500 uppercase">DynamoDB</div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <Metric label="Presence" value={deleteResult.externalCleanup.dynamoPresenceDeleted ?? 0} />
                      <Metric label="User State" value={deleteResult.externalCleanup.dynamoStateDeleted ?? 0} />
                      <Metric label="Messages" value={deleteResult.externalCleanup.dynamoMessagesDeleted ?? 0} />
                      <Metric label="Idempotency" value={deleteResult.externalCleanup.dynamoIdempotencyDeleted ?? 0} />
                    </div>
                    {deleteResult.externalCleanup.dynamoError && (
                      <div className="text-xs text-red-400 mt-2 break-words">{deleteResult.externalCleanup.dynamoError}</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
          {step === 'preview' ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={isLoadingPreview || !!previewError}
                className="px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Proceed to Confirm
              </button>
            </>
          ) : step === 'confirm' ? (
            <>
              <button onClick={() => setStep('preview')} disabled={isDeleting} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50">
                Back
              </button>
              <button
                onClick={handleDelete}
                disabled={!canDelete || isDeleting}
                className="px-4 py-2 text-sm bg-red-800 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 border border-red-600"
              >
                {isDeleting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Deleting...
                  </span>
                ) : (
                  'Permanently Delete User'
                )}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                Close
              </button>
              <button
                onClick={onSuccess}
                className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg transition-colors"
              >
                Back to Users
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-slate-800/70 border border-slate-700/40 rounded p-2 text-center">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-100 font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function PreviewCard({ icon, label, count, detail }: { icon: React.ReactNode; label: string; count: number; detail?: string }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
      count > 0 ? 'bg-red-900/10 border-red-800/30' : 'bg-slate-900/30 border-slate-700/30'
    }`}>
      <div className={count > 0 ? 'text-red-400' : 'text-slate-500'}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-300">{label}</span>
          <span className={`text-sm font-medium ${count > 0 ? 'text-red-400' : 'text-slate-500'}`}>{count}</span>
        </div>
        {detail && <p className="text-xs text-slate-500 truncate">{detail}</p>}
      </div>
    </div>
  );
}
